import { Immutable } from "immer";
import React from "react";

import { ProductCompareTheme } from "../../../constants";
import { IProduct } from "../../../models/catalogue";
import { ISafeHTML, WebPageURL } from "../../../models/nominals";
import {
    IAttributeOptionGroup,
    ICompareTile,
} from "../../../models/product-compare";
import { useViewport } from "../../../store/common/hooks";
import { useAppSelector } from "../../../store/reducers";
import { trackCategoryPageView } from "../../../utils/analytics";
import { notEmpty } from "../../../utils/functional";
// eslint-disable-next-line import/no-restricted-paths -- pre-existing cross-app dependency (Phase E exemption)
import { rootProductSelector } from "../../configurator/selectors";
import { defaults } from "../defaults";
import { getSelectedVariant } from "../selectors";
import { ProductCompareGrid as ProductCompareGridComponent } from "./ProductCompareGridContent";

const trackCategoryView = (
    tiles: readonly ICompareTile[],
    priceSelectValue: string,
): void => {
    const skus = tiles
        .map((tile) => {
            const variant = getSelectedVariant(tile, priceSelectValue);
            return variant ? variant.skus[0] : null;
        })
        .filter(notEmpty);
    trackCategoryPageView(skus);
};

type IProps = Immutable<{
    header: ISafeHTML;
    basketLink: WebPageURL;
    financingLink: WebPageURL;
    preselectedSize: string | null;
    sizeAttributeOptionGroup: IAttributeOptionGroup | null;
    tiles: readonly ICompareTile[];
    theme: ProductCompareTheme;
    showFirstAvailablePrice?: boolean;
    showRating?: boolean;
    showStartingAt?: boolean;
    isMinimal?: boolean;
    returnUrl: string;
}>;

export const ProductCompareGrid = (props: IProps): React.ReactNode => {
    const priceSelectValue = useAppSelector(
        (state) => (state.productcompare || defaults).priceSelectValue,
    );
    const viewport = useViewport();
    const rootProduct: IProduct | null = useAppSelector((state) =>
        rootProductSelector(state.configurator),
    );
    const didMountRef = React.useRef(false);

    React.useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true;
            return;
        }
        trackCategoryView(props.tiles, priceSelectValue);
    }, [props.tiles, priceSelectValue]);

    const pageRootProduct = rootProduct ? rootProduct.id : null;

    return (
        <ProductCompareGridComponent
            header={props.header}
            tiles={props.tiles}
            basketLink={props.basketLink}
            financingLink={props.financingLink}
            sizeAttributeOptionGroup={props.sizeAttributeOptionGroup}
            preselectedSize={props.preselectedSize || null}
            priceSelectValue={priceSelectValue}
            pageRootProduct={pageRootProduct}
            theme={props.theme}
            returnUrl={props.returnUrl}
            viewport={viewport}
            showFirstAvailablePrice={props.showFirstAvailablePrice}
            showRating={props.showRating}
            showStartingAt={props.showStartingAt}
            isMinimal={props.isMinimal}
        />
    );
};
